import React from 'react';

import { ProButton } from '@components/Button/variants';
import LogoTIProWithIconTI from '@components/Images/Rebranding/LogoTIProWithIconTI';
import LazyImgixImage from '@components/LazyImgixImage';
import { useEventTracking } from '@hooks';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';

import { SUBSCRIBE_BUTTON_TEXT, UPGRADE_BUTTON_TEXT, getCTAPath } from './constants';

export const HERO_PHONE_GIF_URL = 'https://tii.imgix.net/global/pro/Hero_IMG.gif';
export const TEXTURE_IMAGE_URL = 'https://tii.imgix.net/global/pro/Texture.png';

interface ProHeroProps {
  proOfferCode: string;
  initialCurrentUser?: User;
  isProSubscriberNotBot: boolean;
}

const ProHero = ({ proOfferCode, initialCurrentUser, isProSubscriberNotBot }: ProHeroProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.ProHero,
  });
  const isSubscriber = initialCurrentUser?.is_subscriber;
  const subscribeButtonText = isSubscriber ? UPGRADE_BUTTON_TEXT : SUBSCRIBE_BUTTON_TEXT;

  return (
    <div className="flex items-center border-0 border-b border-solid border-grayscale-beige pb-48 md:border-0 lg:pb-64">
      <div className="relative hidden h-[536px] w-[350px] flex-shrink-0 bg-[url('https://tii.imgix.net/global/pro/Texture.png')] bg-contain md:block lg:h-[623px] lg:w-[408px]">
        <LazyImgixImage
          className="!flex w-full"
          src={HERO_PHONE_GIF_URL}
          role="presentation"
          wrapperClassName="absolute left-72 top-28 block w-[250px] rounded-36 shadow-pro lg:left-[87px] lg:top-32 lg:w-[290px]"
        />
      </div>
      <div className="md:ml-16">
        <LogoTIProWithIconTI className="max-w-[240px] sm:max-w-[305px] lg:max-w-[370px]" />
        <h1 className="m-0 mt-8 overflow-hidden font-suisse-works text-headline-md text-primary-black sm:mt-36 lg:mt-56">
          <span className="absolute -left-[99999em]">The Information Pro: </span>
          Always be the most informed person in the room.
        </h1>
        <p className="m-0 mt-8 font-suisse-neue text-subheadline-sm text-primary-black sm:mt-[22px] md:mt-[30px] lg:mt-[22px] lg:text-subheadline-md">
          The Information Pro is a subscription that provides access to The Information’s
          proprietary data tools on top of exclusive journalism. We built these tools for our
          newsroom because they didn’t exist anywhere else &mdash; and now we’re sharing them with
          our subscribers.
        </p>
        {!isProSubscriberNotBot && (
          <ProButton
            as="link"
            className="mt-32"
            href={getCTAPath(isSubscriber, proOfferCode)}
            onClickCapture={() => {
              trackEvent(
                click({
                  clickTarget: isSubscriber ? ClickTarget.Upgrade : ClickTarget.Subscribe,
                  clickText: subscribeButtonText,
                })
              );
            }}
          >
            {subscribeButtonText}
          </ProButton>
        )}
      </div>
    </div>
  );
};
export default ProHero;
